import { memo, useCallback, useId, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { ExpandMore as ExpandMoreIcon, MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Accordion, AccordionSummary, IconButton, InputBase, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  useDashboardBuilderEditing,
  useDashboardConfig,
} from '@trinity/chat-components/containers/Dashboard/context';
import { BLOCK_GAP } from './dashboardLayoutUtils';
import { useReportBlockHeight } from './blockAutoHeight';
import { blockEntries, FIXED_ROW_MIN_HEIGHT, PageCell } from './cell';
import { ThemedMenu } from './shared-styled';

// --- Types --------------------------------------------------------------------

type Props = {
  blockId: string;
  title: string;
  /** Whether the block title is hidden in the rendered dashboard (shows in section-title style in the builder). */
  hideTitle: boolean;
  /** Number of grid columns this block spans */
  blockCols: number;
  /** Whether this block is currently collapsed to header-only height. Controlled externally. */
  collapsed: boolean;
  /** Toggle the collapsed state (controlled by BuilderCanvas). */
  onToggleCollapse: () => void;
  /** When true, renders in clean read-only preview mode (no editing chrome). */
  readOnly?: boolean;
  /** When true, the block can be collapsed in readOnly mode (accordion style). */
  collapsible?: boolean;
};

// --- Block Header sub-component ----------------------------------------------

type BlockHeaderProps = {
  readOnly: boolean;
  title: string;
  hideTitle: boolean;
  collapsible: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  handleTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  blockMenuAnchor: null | HTMLElement;
  setBlockMenuAnchor: (el: null | HTMLElement) => void;
  blockMenuId: string;
  blockId: string;
  toggleBlockHideTitle: (id: string) => void;
  toggleBlockCollapsible: (id: string) => void;
  removeBlock: (id: string) => void;
  /** The block's height follows its content (block menu toggle). */
  autoHeight: boolean;
  onToggleAutoHeight: () => void;
};

function BlockHeader({
  readOnly,
  title,
  hideTitle,
  collapsible,
  collapsed,
  onToggleCollapse,
  handleTitleChange,
  blockMenuAnchor,
  setBlockMenuAnchor,
  blockMenuId,
  blockId,
  toggleBlockHideTitle,
  toggleBlockCollapsible,
  removeBlock,
  autoHeight,
  onToggleAutoHeight,
}: BlockHeaderProps): React.ReactElement | null {
  if (readOnly) {
    return renderReadOnlyHeader({ title, hideTitle, collapsible, collapsed, onToggleCollapse });
  }
  return (
    <BuilderHeader
      autoHeight={autoHeight}
      onToggleAutoHeight={onToggleAutoHeight}
      title={title}
      hideTitle={hideTitle}
      collapsible={collapsible}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      handleTitleChange={handleTitleChange}
      blockMenuAnchor={blockMenuAnchor}
      setBlockMenuAnchor={setBlockMenuAnchor}
      blockMenuId={blockMenuId}
      blockId={blockId}
      toggleBlockHideTitle={toggleBlockHideTitle}
      toggleBlockCollapsible={toggleBlockCollapsible}
      removeBlock={removeBlock}
    />
  );
}

function renderReadOnlyHeader({
  title,
  hideTitle,
  collapsible,
  collapsed,
  onToggleCollapse,
}: Pick<
  BlockHeaderProps,
  'title' | 'hideTitle' | 'collapsible' | 'collapsed' | 'onToggleCollapse'
>): React.ReactElement | null {
  if (hideTitle || !title) return null;
  if (collapsible) {
    return (
      <Accordion
        expanded={!collapsed}
        onChange={() => onToggleCollapse()}
        sx={{
          border: 'none',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            width: '100%',
            alignItems: 'center',
            '& .MuiAccordionSummary-content': { alignItems: 'center' },
            '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
            '& .MuiAccordionSummary-expandIconWrapper': { alignSelf: 'center' },
          }}
        >
          <Typography
            fontSize="20px"
            sx={{ flexGrow: 1 }}
            fontFamily="var(--font-bold, inherit)"
            fontWeight={700}
            color="var(--black-l, #323232)"
          >
            {title}
          </Typography>
        </AccordionSummary>
      </Accordion>
    );
  }
  return (
    <PreviewBlockHeader>
      <Typography
        noWrap
        sx={{ flex: 1, fontSize: '20px', fontWeight: 700, fontFamily: 'var(--font-bold, inherit)' }}
      >
        {title}
      </Typography>
    </PreviewBlockHeader>
  );
}

function BuilderHeader({
  title,
  hideTitle,
  collapsible,
  collapsed,
  onToggleCollapse,
  handleTitleChange,
  blockMenuAnchor,
  setBlockMenuAnchor,
  blockMenuId,
  blockId,
  toggleBlockHideTitle,
  toggleBlockCollapsible,
  removeBlock,
  autoHeight,
  onToggleAutoHeight,
}: Omit<BlockHeaderProps, 'readOnly'>): React.ReactElement {
  const iconSize = hideTitle ? 12 : 16;
  const btnPadding = hideTitle ? '1px' : undefined;
  return (
    <ShellHeader className="drag-handle" collapsed={collapsed} hideTitle={hideTitle}>
      <TitleInput
        hideTitle={hideTitle}
        value={title}
        onChange={handleTitleChange}
        placeholder="Block name..."
        inputProps={{ 'aria-label': 'Block title' }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setBlockMenuAnchor(e.currentTarget);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="Block actions"
        aria-controls={blockMenuAnchor ? blockMenuId : undefined}
        aria-haspopup="true"
        sx={{ flexShrink: 0, padding: btnPadding }}
      >
        <MoreHorizIcon fontSize={hideTitle ? 'inherit' : 'small'} />
      </IconButton>
      <ThemedMenu
        id={blockMenuId}
        anchorEl={blockMenuAnchor}
        open={Boolean(blockMenuAnchor)}
        onClose={() => setBlockMenuAnchor(null)}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => {
            setBlockMenuAnchor(null);
            toggleBlockHideTitle(blockId);
          }}
        >
          {hideTitle ? 'Show title' : 'Hide title'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setBlockMenuAnchor(null);
            toggleBlockCollapsible(blockId);
          }}
        >
          {collapsible ? 'Remove collapsible' : 'Make collapsible'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setBlockMenuAnchor(null);
            onToggleAutoHeight();
          }}
        >
          {autoHeight ? 'Use fixed row height' : 'Fit height to content'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setBlockMenuAnchor(null);
            removeBlock(blockId);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete block
        </MenuItem>
      </ThemedMenu>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggleCollapse();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={collapsed ? 'Expand block' : 'Collapse block'}
        sx={{ flexShrink: 0, ml: 0.25, padding: btnPadding }}
      >
        {collapsed ? <ChevronDown size={iconSize} /> : <ChevronUp size={iconSize} />}
      </IconButton>
    </ShellHeader>
  );
}

// --- WidgetCardShell -----------------------------------------------------------

/**
 * One block on the canvas: its header (title, menu, collapse) and its content, laid out by the page
 * builder's cell (see cell/PageCell): widgets and sections in rows and side-by-side slots, rows as
 * tall as their content (or a dragged height), drag and drop to any cell. The block reports its
 * content height so the canvas sizes it to fit.
 */
export const WidgetCardShell = memo(function WidgetCardShell({
  blockId,
  title,
  hideTitle,
  blockCols,
  collapsed,
  onToggleCollapse,
  readOnly = false,
  collapsible = false,
}: Props): React.ReactElement {
  const { config } = useDashboardConfig();
  const { removeBlock, updateBlockTitle, toggleBlockHideTitle, toggleBlockCollapsible, updateBlockSizing } =
    useDashboardBuilderEditing();
  const block = config.blocks.find((b) => b.id === blockId);
  // Fit-to-content is the default; only an explicit `autoHeight: false` keeps fixed rows.
  const autoHeight = block?.autoHeight !== false;
  const entries = useMemo(() => (block ? blockEntries(block) : []), [block]);
  // The shell's 1 px top and bottom border sit outside the measured content.
  const measureRef = useReportBlockHeight<HTMLDivElement>(blockId, true, 2);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => updateBlockTitle(blockId, e.target.value),
    [updateBlockTitle, blockId],
  );

  const blockMenuId = useId();
  const [blockMenuAnchor, setBlockMenuAnchor] = useState<null | HTMLElement>(null);

  return (
    <Shell scrolls={block?.maxHeight !== undefined}>
      <ShellContent ref={measureRef} autoHeight>
        <BlockHeader
          readOnly={readOnly}
          title={title}
          hideTitle={hideTitle}
          collapsible={collapsible}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          handleTitleChange={handleTitleChange}
          blockMenuAnchor={blockMenuAnchor}
          setBlockMenuAnchor={setBlockMenuAnchor}
          blockMenuId={blockMenuId}
          blockId={blockId}
          toggleBlockHideTitle={toggleBlockHideTitle}
          toggleBlockCollapsible={toggleBlockCollapsible}
          removeBlock={removeBlock}
          autoHeight={autoHeight}
          onToggleAutoHeight={() =>
            // Switching either way clears the floor, ceiling and dragged row heights.
            updateBlockSizing(blockId, {
              autoHeight: autoHeight ? false : undefined,
              minHeight: undefined,
              maxHeight: undefined,
              rowHeights: undefined,
            })
          }
        />
        {!collapsed && (
          <PageCell
            container={{ blockId }}
            entries={entries}
            cols={blockCols}
            editing={!readOnly}
            rowHeights={autoHeight ? block?.rowHeights : undefined}
            // A fixed-row block keeps the legacy row unit as each row's floor; rows still grow to fit.
            rowMin={autoHeight ? undefined : FIXED_ROW_MIN_HEIGHT}
            rowResize={autoHeight}
            allowSections
          />
        )}
      </ShellContent>
    </Shell>
  );
});

// --- Styled components --------------------------------------------------------

const Shell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'scrolls',
})<{ scrolls: boolean }>(({ theme, scrolls }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Fill the RGL item but leave BLOCK_GAP at the bottom as the gap
  // between blocks.  The RGL item height always bakes in that gap.
  height: `calc(100% - ${BLOCK_GAP}px)`,
  width: '100%',
  border: `1px solid ${theme.palette.gray.light2}`,
  borderRadius: '12px',
  background: theme.palette.white.main,
  // An auto-height block capped by maxHeight scrolls its content.
  overflowX: 'hidden',
  overflowY: scrolls ? 'auto' : 'hidden',
}));

/**
 * The shell's content. Fixed blocks stretch it to the shell; auto-height blocks size it to their
 * rows and measure it, so it must never take its height from the grid item.
 */
const ShellContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'autoHeight',
})<{ autoHeight: boolean }>(({ autoHeight }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: autoHeight ? 'none' : 1,
}));

const ShellHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed' && prop !== 'hideTitle',
})<{ collapsed: boolean; hideTitle: boolean }>(({ theme, collapsed, hideTitle }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
  flexShrink: 0,
  ...(hideTitle
    ? {
        padding: '2px 4px 2px 8px',
        height: '24px',
        backgroundColor: `${theme.palette.primary.main}18`,
        backdropFilter: 'blur(4px)',
        borderBottom: `1px solid ${theme.palette.primary.light1}`,
      }
    : {
        padding: '14px 8px 8px 12px',
        borderBottom: collapsed ? 'none' : `1px solid ${theme.palette.gray.light2}`,
        minHeight: '40px',
      }),
}));

const TitleInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== 'hideTitle',
})<{ hideTitle: boolean }>(({ theme, hideTitle }) => ({
  flex: 1,
  ...(hideTitle
    ? {
        marginRight: '26px',
        '& input': {
          padding: 0,
          cursor: 'text',
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 700,
          color: theme.palette.text.primary,
          lineHeight: 1,
        },
      }
    : {
        borderBottom: `2px solid ${theme.palette.text.disabled}`,
        '& input': {
          padding: 0,
          cursor: 'text',
          fontSize: theme.typography.h6.fontSize,
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
      }),
}));

const PreviewBlockHeader = styled('div')(({ theme }) => ({
  padding: '10px 16px 6px',
  flexShrink: 0,
}));
